<template>
  <tr class="group hover:bg-gray-50 transition-colors duration-200 child:py-3 child:px-2 child:border-b child:border-gray-100">
    <td class="">
      <div class="flex items-center justify-center space-x-2">
        <input
          v-if="projectsStore.project?.unarchived"
          type="checkbox"
          name="approve"
          :checked="!!record.user"
          class="form-checkbox w-4 h-4 text-blue-600"
          data-test="approve-labour"
          :aria-label="$t('SelfPerform.LabourDashboard.LabourList.LabourListRow.LabourRowRecord.accessibility.approveLabour')"
          title="Approve Record"
          @input="toggleApprove($event as InputEvent, record)"
        >

        <button
          v-if="!record.user && projectsStore.project?.unarchived"
          type="button"
          class="btn-icon btn-icon-blue"
          data-test="edit"
          :aria-label="$t('SelfPerform.LabourDashboard.LabourList.LabourListRow.LabourRowRecord.accessibility.editRecord')"
          title="Edit Record"
          @click="$emit('selectRecord', $props.record.id)"
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
        </button>

        <button
          type="button"
          class="btn-icon btn-icon-default"
          data-test="show-comments"
          :aria-label="$t('SelfPerform.LabourDashboard.LabourList.LabourListRow.LabourRowRecord.accessibility.showComments')"
          title="Show Comments"
          @click="$emit('showComments', $props.record.id)"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>
      </div>
    </td>

    <td class="">
      <div class="flex flex-col">
        <p class="font-medium text-gray-900">
          {{ formatRecordApproved(record)?.name }}
        </p>

        <p class="text-sm text-gray-500">
          {{ formatRecordApproved(record)?.date }}
        </p>
      </div>
    </td>

    <td class=" text-gray-700">
      {{ new Date(record.day).toLocaleDateString() }}
    </td>

    <td
      class=""
      :title="record.wbsCode?.code"
    >
      <div class="flex flex-col">
        <p class="font-medium text-gray-900">
          {{ record.wbsCode?.code }}
        </p>

        <p class="text-sm text-gray-500 line-clamp-1 max-w-[200px]">
          {{ record.wbsCode?.description }}
        </p>
      </div>
    </td>

    <td class=" text-gray-700">
      {{ record.area }}
    </td>

    <td class=" text-gray-700">
      {{ record.element }}
    </td>

    <td class=" text-gray-700">
      {{ record.activity }}
    </td>

    <td class="text-gray-700">
      <div class="flex flex-col">
        <p class="font-medium">
          {{ formatStart(record.startAt) }}
        </p>

        <p class="text-sm">
          {{ formatEnd(record.endAt, record) }}
        </p>
      </div>
    </td>

    <td class="text-right text-gray-700 font-medium">
      {{ record.hours.toFixed(2) }}
    </td>

    <td class="text-right font-medium text-gray-900">
      {{ australianCurrency(record.accrual) }}
    </td>

    <td class="">
      <div
        v-if="!projectsStore.project?.unarchived"
        class="text-right"
      >
        <div
          v-if="record.paid"
          class="font-medium text-gray-900"
        >
          {{ australianCurrency(record.paid) }}
        </div>
      </div>

      <div
        v-else-if="typingPaid || !record.paid"
        class="flex justify-end"
      >
        <form @submit.prevent="submitForm(record)">
          <AppCurrencyInput
            ref="paidInput"
            v-model="paid"
            name="paid"
            class="form-input w-24 h-8 text-sm"
            data-test="labour-paid"
            :aria-label="$t('SelfPerform.LabourDashboard.LabourList.LabourListRow.LabourRowRecord.accessibility.editPaidAmount')"
            @blur="submitForm(record)"
          />
        </form>
      </div>

      <div
        v-else
        class="text-right cursor-pointer"
        data-test="record-paid-text"
        @click="toggleTypingPaid"
      >
        <div class="flex items-center justify-end space-x-1">
          <button
            type="button"
            class="btn-icon btn-icon-default"
            :aria-label="$t('SelfPerform.LabourDashboard.LabourList.LabourListRow.LabourRowRecord.accessibility.editPaidAmount')"
          >
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
          </button>

          <span class="font-medium text-gray-900">{{ australianCurrency(record.paid) }}</span>
        </div>
      </div>
    </td>

    <td class="">
      <div
        v-if="!projectsStore.project?.unarchived"
        class="text-center text-gray-700"
      >
        {{ record.vendor_ref }}
      </div>

      <div
        v-else-if="typingVendor || !record.vendor_ref"
        class="flex justify-center"
      >
        <form @submit.prevent="submitVendor(record)">
          <AppInput
            ref="vendorInput"
            v-model="vendor"
            name="vendor"
            type="text"
            data-test="record-vendor"
            class="form-input w-24 h-8 text-sm"
            :aria-label="$t('SelfPerform.LabourDashboard.LabourList.LabourListRow.LabourRowRecord.accessibility.editVendorRef')"
            @blur="submitVendor(record)"
          />
        </form>
      </div>

      <div
        v-else
        class="text-center cursor-pointer"
        data-test="record-vendor-text"
        @click="toggleTypingVendor"
      >
        <div class="flex items-center justify-center space-x-1">
          <button
            type="button"
            class="btn-icon btn-icon-default"
            :aria-label="$t('SelfPerform.LabourDashboard.LabourList.LabourListRow.LabourRowRecord.accessibility.editVendorRef')"
          >
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
          </button>

          <span class="text-gray-700">{{ record.vendor_ref }}</span>
        </div>
      </div>
    </td>
  </tr>
</template>

<script setup lang="ts">
import { australianCurrency } from '@/helpers';
import { t } from '@/i18n';
import Labour from '@/models/Labour';
import LabourDailyRecord from '@/models/LabourDailyRecord';
import { useLabourDailyRecordStore } from '@/store/labourDailyRecord';
import { useProjectsStore } from '@/store/projects';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';
import { nextTick, ref } from 'vue';

const props = defineProps<{
  record: LabourDailyRecord;
  labour: Labour;
}>();

defineEmits<{
  (e: 'selectRecord', id: Id);
  (e: 'showComments', id: Id);
}>();

const paid = ref(props.record.paid);
const typingPaid = ref(false);
const paidInput = ref(undefined);

const toggleTypingPaid = () => {
  typingPaid.value = !typingPaid.value;

  if(typingPaid.value) {
    nextTick(() => {
      paidInput.value?.focusInput();
    });
  }
};

const labourDailyRecordStore = useLabourDailyRecordStore();

const toggleApprove = (event: InputEvent, record: LabourDailyRecord) => {
  if((event.target as HTMLInputElement).checked) {
    labourDailyRecordStore.approve(record.id);
  } else {
    if(record.paid) {
      Swal.fire({
        icon: 'warning',
        text: t('SelfPerform.LabourDashboard.LabourList.LabourListRow.LabourRowRecord.paidRecordWarning'),
        showCancelButton: true,
      }).then((result) => {
        if(result.isConfirmed) {
          labourDailyRecordStore.unapprove(record.id);
        } else {
          (event.target as HTMLInputElement).checked = true;
        }
      });
    } else {
      labourDailyRecordStore.unapprove(record.id);
    }
  }
};

const formatRecordApproved = (record: LabourDailyRecord) => {
  if(record.user) {
    return {
      name: record.user.name,
      date: record.approvedAt?.toLocaleDateString('en-AU'),
    };
  }

  return undefined;
};

const submitForm = (record: LabourDailyRecord) => {
  labourDailyRecordStore.paid(record.id, { paid: paid.value }).then(() => {
    typingPaid.value = false;
  });
};

const formatStart = (date: DateTime | undefined) => {
  if(date) {
    return date.toLocaleString({
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    });
  }
};

const formatEnd = (date: DateTime | undefined, record: LabourDailyRecord) => {
  if(date) {
    const isOvernight = Boolean(
      record.startAt &&
        record.endAt &&
        !record.endAt.hasSame(record.startAt, 'day'),
    );

    if(isOvernight) {
      return date.toLocaleString({
        day: '2-digit',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
        month: '2-digit',
      });
    }

    return date.toLocaleString({
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    });
  }
};

const vendor = ref(props.record.vendor_ref);
const typingVendor = ref(false);
const vendorInput = ref(undefined);

const toggleTypingVendor = () => {
  typingVendor.value = !typingVendor.value;

  if(typingVendor.value) {
    nextTick(() => {
      vendorInput.value?.focusInput();
    });
  }
};

const submitVendor = (record: LabourDailyRecord) => {
  labourDailyRecordStore.vendor(record.id, { vendor_ref: vendor.value }).then(() => {
    typingVendor.value = false;
  });
};

const projectsStore = useProjectsStore();
</script>

<style scoped></style>
